import './main'; // Import auth and API setup
import { initializePage } from './components/template';
import { initializeAnnouncements } from './components/announcements';
import { initializeNoticesChecking } from './components/notices';
import { createPreferencesDialog, openPreferencesDialog } from './components/preferences';
import { login, logout, getUser } from './auth';
import { apiCall } from './api';

interface UserPreferences {
  display_name: string;
  default_timezone: string;
}

// Cache key for storing user preferences
const USER_PREFS_CACHE_KEY = 'user_preferences_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedPreferences {
  data: UserPreferences;
  timestamp: number;
  userId: string;
}

function getCachedPreferences(userId: string): UserPreferences | null {
  try {
    const cached = localStorage.getItem(USER_PREFS_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedPreferences = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is valid (same user and not expired)
    if (parsed.userId === userId && (now - parsed.timestamp) < CACHE_DURATION) {
      return parsed.data;
    }

    // Clear invalid cache
    localStorage.removeItem(USER_PREFS_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

function setCachedPreferences(userId: string, preferences: UserPreferences): void {
  try {
    const cacheData: CachedPreferences = {
      data: preferences,
      timestamp: Date.now(),
      userId
    };
    localStorage.setItem(USER_PREFS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache user preferences:', error);
  }
}

function clearPreferencesCache(): void {
  localStorage.removeItem(USER_PREFS_CACHE_KEY);
}

async function fetchUserPreferences(userId: string): Promise<UserPreferences | null> {
  // Try to get from cache first
  const cached = getCachedPreferences(userId);
  if (cached) {
    return cached;
  }

  // Fetch from API
  try {
    const prefs = await apiCall<UserPreferences>('GET', '/api/user-preferences/');
    // Cache the result
    if (prefs) {
      setCachedPreferences(userId, prefs);
    }
    return prefs;
  } catch (error) {
    console.error('Failed to fetch user preferences:', error);
    return null;
  }
}

function setupDisplayNameDisplay(whoamiContainer: HTMLElement, displayName: string) {
  whoamiContainer.innerHTML = '';

  // Create display name span (simple, non-editable)
  const displayNameSpan = document.createElement('strong');
  displayNameSpan.id = 'display-name';
  displayNameSpan.textContent = displayName;

  whoamiContainer.appendChild(displayNameSpan);
}

async function refreshUserDisplay() {
  const whoami = document.getElementById('whoami') as HTMLElement;
  const loginBtn = document.getElementById('login') as HTMLElement;
  const logoutBtn = document.getElementById('logout') as HTMLElement;
  const authRequiredElements = document.querySelectorAll('.auth-required');

  const user = await getUser();
  if (user) {
    const userId = user.profile?.sub || user.profile?.email || '';

    // Fetch user preferences to get display name (with caching)
    const preferences = await fetchUserPreferences(userId);
    const displayName = preferences?.display_name || user.profile?.email || '(signed in)';

    if (whoami) {
      setupDisplayNameDisplay(whoami, displayName);
    }

    if (loginBtn) loginBtn.style.display = 'none';
    if (logoutBtn) logoutBtn.style.display = 'inline-block';

    // Show auth-required navigation items
    authRequiredElements.forEach(element => {
      if (element instanceof HTMLElement) {
        // For the inbox wrapper div, use inline-block to maintain position:relative
        if (element.querySelector('#nav-inbox')) {
          element.style.display = 'inline-block';
        } else {
          element.style.display = 'inline-block';
        }
      }
    });
  } else {
    // Clear cache on logout
    clearPreferencesCache();

    if (whoami) whoami.textContent = 'anonymous';
    if (loginBtn) loginBtn.style.display = 'inline-block';
    if (logoutBtn) logoutBtn.style.display = 'none';

    // Hide auth-required navigation items
    authRequiredElements.forEach(element => {
      if (element instanceof HTMLElement) {
        element.style.display = 'none';
      }
    });
  }
}

// Ensure Shoelace components are ready
async function waitForShoelace(): Promise<void> {
  const criticalComponents = ['sl-button', 'sl-icon', 'sl-badge', 'sl-spinner', 'sl-dialog', 'sl-select', 'sl-option'];
  const promises = criticalComponents.map(tag => customElements.whenDefined(tag));
  await Promise.all(promises);

  // Add ready class if not already present
  if (!document.body.classList.contains('sl-ready')) {
    document.body.classList.add('sl-ready');
  }
}

// Enhanced navigation with View Transitions API
export function navigateWithTransition(href: string): void {
  // Check if View Transitions API is supported
  if ('startViewTransition' in document) {
    // @ts-ignore - View Transitions API may not be in TypeScript definitions yet
    document.startViewTransition(() => {
      window.location.href = href;
    });
  } else {
    // Fallback to regular navigation
    window.location.href = href;
  }
}

export function initializeSharedComponents(currentPage: 'index' | 'keys' | 'images' | 'inbox' | 'dashboard' | 'docs' | 'chat' | 'usage', skipNoticesCheck = false): void {
  // Wait for Shoelace to be ready
  waitForShoelace().then(() => {
    // Mark body as ready (in case the head script didn't run)
    document.body.classList.add('sl-ready');
  });

  // Initialize page layout
  initializePage({ currentPage });

  // Initialize announcements (it will check auth status internally)
  initializeAnnouncements();

  // Initialize notices checking (skip on inbox page to avoid redundancy)
  if (!skipNoticesCheck) {
    // Lazily check for unread notices after page loads
    setTimeout(async () => {
      const user = await getUser();
      if (user) {
        await initializeNoticesChecking();
      }
    }, 500);
  }

  // Auto-create debug section in development mode (but keep it hidden initially)
  if (import.meta.env.DEV) {
    createDebugSection();
  }

  // Initialize preferences dialog
  const preferencesDialog = createPreferencesDialog();
  preferencesDialog.id = 'preferences-dialog';
  document.body.appendChild(preferencesDialog);

  // Set up preferences button click handler
  const preferencesBtn = document.getElementById('preferences-btn');
  if (preferencesBtn) {
    preferencesBtn.addEventListener('click', () => openPreferencesDialog());
  }

  // Listen for preferences updates to refresh display name
  window.addEventListener('preferences-updated', () => {
    refreshUserDisplay();
  });

  // Set up auth button click handlers
  const loginBtn = document.getElementById('login');
  const logoutBtn = document.getElementById('logout');

  if (loginBtn) {
    loginBtn.addEventListener('click', () => login());
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => logout());
  }

  // Enhanced navigation for all navigation buttons
  const navButtons = document.querySelectorAll('header sl-button[href]');
  navButtons.forEach(button => {
    const href = button.getAttribute('href');
    if (href && !href.startsWith('#')) {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        navigateWithTransition(href);
      });
    }
  });

  // Refresh user display
  refreshUserDisplay();

  // Listen for auth state changes
  window.addEventListener('auth-state-changed', () => {
    refreshUserDisplay();
  });
}

export function createDebugSection(): void {
  const existingDebug = document.getElementById('debug');
  if (!existingDebug) {
    const main = document.querySelector('main');
    if (main) {
      const section = document.createElement('section');
      section.id = 'debug-section';
      section.style.marginTop = '16px';
      section.style.display = 'none'; // Hidden by default
      section.innerHTML = `
        <h3>Debug</h3>
        <pre id="debug" style="background:#f6f6f6; padding:12px; border-radius:8px; overflow:auto"></pre>
      `;
      main.appendChild(section);
    }
  }
}

export function updateDebug(content: string): void {
  // First ensure debug section exists
  if (!document.getElementById('debug')) {
    createDebugSection();
  }
  
  const debug = document.getElementById('debug') as HTMLPreElement | null;
  const debugSection = document.getElementById('debug-section') || debug?.closest('section');
  
  if (debug && content) {
    debug.textContent = content;
    // Show the debug section when there's content
    if (debugSection) {
      debugSection.style.display = 'block';
    }
  } else if (debug && !content && debugSection) {
    // Hide if content is empty
    debugSection.style.display = 'none';
  }
}